/* eslint-disable typescript-sort-keys/interface */
import {
  type ComponentType,
  type Context,
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';

export interface Breakpoints {
  desktop: number;
  desktopLg: number;
  desktopMd: number;
  phoneLg: number;
  phoneSm: number;
  tablet: number;
}

export const breakpoints: Breakpoints = {
  desktop: 1025,
  desktopLg: 1360,
  desktopMd: 1200,
  phoneLg: 600,
  phoneSm: 320,
  tablet: 768,
};

export interface Media {
  xs: 376;
  s: 737;
  m: 981;
  l: 1025;
  xl: 1281;
  xxl: 1361;
}

export const media: Media = {
  l: 1025,
  m: 981,
  s: 737,
  xl: 1281,
  xs: 376,
  xxl: 1361,
};

export const isDesktopQuery = '(min-width: 1025px)';

/** @deprecated Identical to `isDesktopQuery` now that desktop starts at 1025px. */
export const isJustDesktopQuery = isDesktopQuery;

export const isTabletQuery = '(min-width: 768px) and (max-width: 1024px)';

export const isTabletOrDesktopQuery = '(min-width: 768px)';

export const isTabletOrMobileQuery = '(max-width: 1024px)';

/** One pixel under the tablet breakpoint, derived so the two cannot drift. */
export const isBelowTabletQuery = `(max-width: ${breakpoints.tablet - 1}px)`;

export const isMobileQuery = '(max-width: 599px)';

export const isSmallMobileQuery = '(max-width: 375px)';

interface MatchMediaSeedValue {
  fixed: boolean;
  width: number;
}

// Published module bundles each inline their own copy of this file; a provider in one copy must reach
// a consumer in another.
const CONTEXT_KEY = Symbol.for('cx.match-media-seed');
const registry = globalThis as { [CONTEXT_KEY]?: Context<MatchMediaSeedValue | undefined> };
const MatchMediaSeedContext = (registry[CONTEXT_KEY] ??= createContext<MatchMediaSeedValue | undefined>(undefined));

export function widthForDevice(deviceType?: string): number {
  if (deviceType === 'MOBILE') return breakpoints.phoneSm;
  if (deviceType === 'TABLET') return breakpoints.tablet;
  return breakpoints.desktop;
}

const WIDTH_FEATURE = /^\(\s*(min|max)-width\s*:\s*(\d+(?:\.\d+)?)px\s*\)$/i;

const warnedQueries = new Set<string>();

/**
 * Evaluates `(min-width: Npx)` / `(max-width: Npx)` joined by `and`. Any other query is unmatched, with a
 * one-time warning: the browser evaluates it properly, so the server render and the corrected one differ.
 */
export function matchesWidth(query: string, width: number): boolean {
  return query.split(/\s+and\s+/i).every(part => {
    const feature = WIDTH_FEATURE.exec(part.trim());
    if (!feature) {
      if (!warnedQueries.has(query)) {
        warnedQueries.add(query);
        console.warn(
          `useMatchMedia: cannot evaluate "${query}" without a viewport; only (min|max)-width in px joined by "and" are supported, so the server render treats it as unmatched.`,
        );
      }
      return false;
    }
    const limit = Number(feature[2]);
    return feature[1].toLowerCase() === 'min' ? width >= limit : width <= limit;
  });
}

const readViewport = (query: string): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;

const noop = () => {};

/**
 * Seeds `useMatchMedia` with the device the server classified the request as, so the server render and
 * the first hydration render evaluate every query against the same width. The browser's real viewport
 * takes over straight after hydration. With no `deviceType` both renders assume desktop. `fixed` pins
 * the width for good, to force a layout.
 */
export function MatchMediaSeed({
  children,
  deviceType,
  fixed = false,
}: {
  children: ReactNode;
  deviceType?: string;
  fixed?: boolean;
}) {
  const width = widthForDevice(deviceType);
  const value = useMemo<MatchMediaSeedValue>(() => ({ fixed, width }), [fixed, width]);

  return <MatchMediaSeedContext.Provider value={value}>{children}</MatchMediaSeedContext.Provider>;
}

/** Without a `MatchMediaSeed` above it, the server and the hydration render answer `false`, then the viewport corrects it. */
export function useMatchMedia(query: string): boolean {
  const seed = useContext(MatchMediaSeedContext);
  const fixedWidth = seed?.fixed ? seed.width : undefined;
  const seededWidth = seed?.width;

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (fixedWidth !== undefined || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return noop;
      }
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [fixedWidth, query],
  );

  const getSnapshot = useCallback(
    () => (fixedWidth === undefined ? readViewport(query) : matchesWidth(query, fixedWidth)),
    [fixedWidth, query],
  );

  const getServerSnapshot = useCallback(
    () => seededWidth !== undefined && matchesWidth(query, seededWidth),
    [query, seededWidth],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export interface ResponsiveProps {
  deviceType?: string; // MOBILE, TABLET, DESKTOP
}

export function withMediaQueries<T = Record<string, unknown>>(
  Component: ComponentType<ResponsiveProps & T>,
): (props: ResponsiveProps & T) => JSX.Element {
  const ResponsiveComponent = (props: ResponsiveProps & T) => (
    <MatchMediaSeed deviceType={props.deviceType}>
      <Component {...props} />
    </MatchMediaSeed>
  );

  return ResponsiveComponent;
}
